import { useEffect, useState } from "react";
import { HardDriveDownload, ShieldCheck, AlertTriangle, RefreshCw, Cloud, Server } from "lucide-react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";

const quando = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)} alle ${s.slice(11, 16)}` : "mai");

/** Stato delle copie dei dati: ultima riuscita, quanto ha pesato, e la prova di ripristino. */
export const BackupStato = ({ client }) => {
  const [st, setSt] = useState(null);
  const [corso, setCorso] = useState(false);
  const [prova, setProva] = useState(null);
  const [spazio, setSpazio] = useState(null);

  const carica = async () => {
    try {
      const r = await client.get("/admin/backup-stato");
      setSt(r.data);
    } catch {
      /* la Console resta aperta anche se un giro va a vuoto */
    }
  };
  useEffect(() => {
    carica();
    const i = setInterval(carica, 120000);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  if (!st) return null;
  const u = st.ultimo;
  const male = st.allarme;

  return (
    <Card className={`p-5 text-slate-100 ${male ? "border-2 border-rose-500 bg-rose-950/40" : "border-slate-800 bg-[#111827]"}`}
          data-testid="console-backup-stato">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">
          <HardDriveDownload className="h-4 w-4" /> Backup
        </p>
        <Button size="sm" variant="outline" disabled={corso} data-testid="backup-verifica-btn"
                onClick={async () => {
                  setCorso(true);
                  try {
                    await client.post("/admin/backup-verifica");
                    await carica();
                  } finally {
                    setCorso(false);
                  }
                }}>
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${corso ? "animate-spin" : ""}`} /> Verifica ora
        </Button>
      </div>

      <p className={`mt-2 text-xl font-bold ${male ? "text-rose-300" : ""}`} data-testid="backup-ultimo">
        {u ? `Ultimo backup ${quando(u.at)}` : "Nessun backup registrato"}
      </p>
      {male && (
        <p className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-rose-300" data-testid="backup-allarme">
          <AlertTriangle className="h-4 w-4" />
          {u && !u.ok ? "L'ultimo backup non è riuscito" : `Più di ${st.soglia_ore} ore senza copie`}
        </p>
      )}

      {u && (
        <dl className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          {[
            ["Esito", u.ok ? "Riuscito" : "Fallito"],
            ["Locali copiati", u.locali],
            ["Dimensione", u.kb >= 1024 ? `${(u.kb / 1024).toFixed(1)} MB` : `${u.kb} KB`],
            ["Durata", `${u.secondi} s`],
          ].map(([k, v]) => (
            <div key={k} className="rounded-lg bg-slate-900/70 px-3 py-2">
              <dt className="text-[10px] uppercase tracking-wider text-slate-500">{k}</dt>
              <dd className={`font-semibold ${k === "Esito" && !u.ok ? "text-rose-300" : ""}`}>{v}</dd>
            </div>
          ))}
        </dl>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-slate-900/70 px-3 py-2.5 text-sm"
           data-testid="backup-ripristino">
        <ShieldCheck className={`h-4 w-4 ${st.ripristino?.ok ? "text-emerald-400" : "text-amber-400"}`} />
        <span>
          Prova di ripristino: {st.ripristino
            ? `${st.ripristino.ok ? "riuscita" : "non riuscita"} il ${quando(st.ripristino.at)}`
            : "mai eseguita"}
        </span>
        {st.ripristino?.ok && (
          <span className="text-xs text-slate-500">
            {st.ripristino.record} record rimessi su, {st.ripristino.collezioni} gruppi di dati
          </span>
        )}
        {st.ripristino && !st.ripristino.ok && st.ripristino.nota && (
          <span className="text-xs text-amber-300">{st.ripristino.nota}</span>
        )}
      </div>
      <p className="mt-2 text-xs text-slate-500">
        {st.copie_disponibili} copie disponibili al momento. La verifica rimette i dati su una copia
        temporanea e la cancella subito dopo.
      </p>

      {st.fuori && (
        <div className="mt-3 rounded-lg bg-slate-900/70 px-3 py-2.5 text-sm" data-testid="backup-fuori">
          <div className="flex flex-wrap items-center gap-2">
            <Cloud className={`h-4 w-4 ${st.fuori.ultima ? "text-emerald-400" : "text-amber-400"}`} />
            <span className="font-semibold">Copia fuori dal database</span>
            <span className="text-slate-300">
              {st.fuori.ultima
                ? `ultima il ${st.fuori.ultima.giorno.slice(8, 10)}/${st.fuori.ultima.giorno.slice(5, 7)}, ${st.fuori.ultima.kb} KB`
                : "nessuna copia esterna ancora salvata"}
            </span>
            <Button size="sm" variant="outline" disabled={corso} data-testid="copia-esterna-prova-btn"
                    className="ml-auto"
                    onClick={async () => {
                      setCorso(true);
                      try {
                        const r = await client.post("/admin/copia-esterna/prova");
                        setProva(r.data);
                        await carica();
                      } finally {
                        setCorso(false);
                      }
                    }}>
              Prova l'impianto esterno
            </Button>
          </div>
          <p className="mt-1.5 text-xs text-slate-500">
            {st.fuori.riuscite} file salvati fuori, {st.fuori.fallite} non riusciti · spazio usato{" "}
            {st.fuori.spazio_mb} MB su {st.fuori.spazio_max_mb} MB. I file sono cifrati: senza la tua
            chiave non si leggono.
            {st.fuori.riletta
              ? ` Ultima copia riletta dall'archivio esterno: ${st.fuori.riletta.giorno.slice(8, 10)}/${st.fuori.riletta.giorno.slice(5, 7)}.`
              : " Nessuna copia esterna ancora riletta."}
          </p>
          {prova && (
            <p className={`mt-1.5 text-xs font-semibold ${prova.ok ? "text-emerald-300" : "text-rose-300"}`}
               data-testid="copia-esterna-prova-esito">
              {prova.ok ? "Prova riuscita: il file è stato scritto fuori e riletto correttamente."
                        : `Prova non riuscita: ${prova.nota}`}
            </p>
          )}
        </div>
      )}
      {st.spazio && (
        <div className="mt-3 rounded-lg bg-slate-900/70 px-3 py-2.5 text-sm" data-testid="backup-spazio-mio">
          <div className="flex flex-wrap items-center gap-2">
            <Server className={`h-4 w-4 ${st.spazio.attivo ? (st.spazio.ultima ? "text-emerald-400" : "text-amber-400") : "text-slate-500"}`} />
            <span className="font-semibold">Copia nel tuo spazio</span>
            <span className="text-slate-300">
              {!st.spazio.attivo
                ? "nessuno spazio collegato: mancano le chiavi"
                : st.spazio.ultima
                  ? `ultima il ${st.spazio.ultima.giorno.slice(8, 10)}/${st.spazio.ultima.giorno.slice(5, 7)}, ${st.spazio.ultima.kb} KB`
                  : "collegato, nessuna copia ancora salvata"}
            </span>
            <Button size="sm" variant="outline" disabled={corso} data-testid="spazio-mio-prova-btn"
                    className="ml-auto"
                    onClick={async () => {
                      setCorso(true);
                      try {
                        const r = await client.post("/admin/spazio-mio/prova");
                        setSpazio(r.data);
                        await carica();
                      } finally {
                        setCorso(false);
                      }
                    }}>
              Prova il tuo spazio
            </Button>
          </div>
          <p className="mt-1.5 text-xs text-slate-500">
            {st.spazio.riuscite} file nel tuo spazio, {st.spazio.fallite} non riusciti ·{" "}
            {st.spazio.mb_totali} MB · teniamo gli ultimi {st.spazio.giorni_conservazione} giorni,
            le copie più vecchie vengono tolte da sole.
          </p>
          {spazio && (
            <p className={`mt-1.5 text-xs font-semibold ${spazio.ok ? "text-emerald-300" : "text-rose-300"}`}
               data-testid="spazio-mio-prova-esito">
              {spazio.ok ? "Prova riuscita: il tuo spazio risponde e accetta i file."
                         : `Prova non riuscita: ${spazio.nota}`}
            </p>
          )}
        </div>
      )}
    </Card>
  );
};
